'use client';

import { useTranslations } from 'next-intl';
import { PERMISSIONS } from '@terra/shared-authorization';
import { Box, VStack } from '@terra/ui-styled-system';
import {
  SettingsContentPanel,
  SettingsEmptyState,
  SettingsPageShell,
} from '@terra/ui-design-system';
import {
  useIsMcpServerTarget,
  useSelectedCompany,
  useTargetType,
  type PortalTarget,
} from '../../../store/projectStore';
import type { ApplicationSettingsTabId } from '../../../store/application-settings-tab-id';
import { TARGET_TYPE } from '../../../utils/target-scope.utils';
import { buildApplicationSettingsWebPath } from '../../../utils/applicationSettingsWebPaths';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { usePermissions } from '../../../hooks/usePermissions';
import { useTenantMcpServers } from '../../../hooks/api/useMcpServers';
import { ErrorBoundary } from '../../ErrorBoundary';
import { SettingsTabHeaderActionsProvider } from '../settingsTabHeaderContext';
import { TestingInstructionsTab } from '../TestingInstructionsTab';
import { TestingGuardrailsTab } from '../TestingGuardrailsTab';
import { MemoriesContent } from '../../tabs/MemoriesContent';
import { AutomationTab } from '../AutomationTab';
import { SkillsSettingsTabContent } from '../SkillsSettingsTabContent';
import { ApplicationMcpServersTab } from '../ApplicationMcpServersTab';
import { AiApplicationMcpConnectionTab } from '../AiApplicationMcpConnectionTab';
import { TargetGeneralPanel } from './TargetGeneralPanel';
import { TargetDomainsPanel } from './TargetDomainsPanel';
import { TargetCredentialsPanel } from './TargetCredentialsPanel';
import { TargetRolesPanel } from './TargetRolesPanel';
import { TargetSignalCategoriesPanel } from './TargetSignalCategoriesPanel';
import { TargetDeploymentDelayPanel } from './TargetDeploymentDelayPanel';
import { TargetCvssPanel } from './TargetCvssPanel';
import { TargetDocumentationPanel } from './TargetDocumentationPanel';
import { TargetKnowledgeSourcesPanel } from './TargetKnowledgeSourcesPanel';
import { ScopingContent } from '../../tabs/ScopingContent';
import { TargetSourceCodePanel } from './TargetSourceCodePanel';
import { TargetNetworkingPanel } from './TargetNetworkingPanel';
import { TargetPreventionPanel } from './TargetPreventionPanel';
import { TargetDangerPanel } from './TargetDangerPanel';
import { useTargetSettingsForm } from './useTargetSettingsForm';
import { useTargetSettingsSection } from './useTargetSettingsSection';
import { useTargetCredentialActions } from './useTargetCredentialActions';
import { useTargetCredentialsSse } from './useTargetCredentialsSse';
import { useTargetRoleActions } from './useTargetRoleActions';
import { TargetSettingsSectionFrame } from './TargetSettingsSectionFrame';

const FILL_HEIGHT_SECTIONS: ReadonlySet<ApplicationSettingsTabId> = new Set([
  'copilot-skills',
  'memories',
  'scoping',
]);

type WebApplicationSettingsTabProps = {
  tabId: string;
  companyId: string | null;
  selectedTarget: PortalTarget | undefined;
  appTargetId: string | undefined;
};

export function WebApplicationSettingsTab({
  tabId,
  companyId,
  selectedTarget,
  appTargetId,
}: WebApplicationSettingsTabProps) {
  const tLayout = useTranslations('layout');
  const tSettings = useTranslations('settings');
  const selectedCompany = useSelectedCompany();
  const tenantId = selectedCompany?.tenantId ?? undefined;
  const applicationId = selectedTarget?.id;
  const { isFeatureFlagEnabled: isMcpServersEnabled } = useFeatureFlags(
    'MCP_SERVERS_ENABLED',
  );
  const { isFeatureFlagEnabled: isPreventionWafEnabled } = useFeatureFlags(
    'PREVENTION_WAF_ENABLED',
  );
  const { hasPermission } = usePermissions();
  const { servers: tenantMcpServers } = useTenantMcpServers();
  const hasApplicationEligibleMcpServers = tenantMcpServers.some((server) =>
    server.allowedTargetTypes.includes('APPLICATION'),
  );
  const isMcpServerTarget = useIsMcpServerTarget();
  const targetType = useTargetType();
  const isAiApplicationTarget = targetType === TARGET_TYPE.AI_APPLICATION;
  const isTargetReadOnly = !hasPermission(PERMISSIONS.MANAGE_APPLICATIONS.name);
  const canDeleteApplication = hasPermission(
    PERMISSIONS.DELETE_APPLICATIONS.name,
  );
  const webSourceCodeSettingsPath = applicationId
    ? buildApplicationSettingsWebPath({
        applicationId,
        isAiApplication: isAiApplicationTarget,
        intent: 'sourceCode',
      })
    : '';
  const hasProjectSelected = Boolean(companyId && appTargetId);
  const form = useTargetSettingsForm({
    selectedTarget,
    tenantId,
    appTargetId,
    isMcpServerTarget,
  });
  const {
    visibleSection,
    setActiveSection,
    activeGroupId,
    selectGroup,
    navItems,
    subTabs,
  } = useTargetSettingsSection({
    tabId,
    isAiApplicationTarget,
    isMcpServersEnabled,
    hasApplicationEligibleMcpServers,
    isMcpServerTarget,
    appTargetId,
    isPreventionEnabled: isPreventionWafEnabled && !isAiApplicationTarget,
    canDeleteApplication,
  });
  const credentialActions = useTargetCredentialActions({
    applicationId,
    tenantId,
  });
  const roleActions = useTargetRoleActions({
    applicationId,
    tenantId,
    refetchRoles: form.refetchRoles,
  });
  const { subscribe } = useTargetCredentialsSse({ applicationId });
  const isFillHeightSection = FILL_HEIGHT_SECTIONS.has(visibleSection);
  const panel = (section: ApplicationSettingsTabId) => ({
    value: section,
    activeValue: visibleSection,
    fillHeight: FILL_HEIGHT_SECTIONS.has(section),
  });

  return (
    <SettingsPageShell
      title={tLayout('applicationSettings')}
      navItems={navItems}
      activeNavItem={activeGroupId}
      onNavItemChange={selectGroup}
    >
      <VStack
        gap="0"
        h={isFillHeightSection ? 'full' : undefined}
        minH={0}
        alignItems="stretch"
      >
        <SettingsTabHeaderActionsProvider>
          <TargetSettingsSectionFrame
            activeSection={visibleSection}
            onSectionChange={setActiveSection}
            subTabs={subTabs}
            fillHeight={isFillHeightSection}
          >
            <ErrorBoundary
              scopeId={`application-settings-${visibleSection}`}
              onClose={() => setActiveSection('general')}
              dismissActionLabel={tSettings('settingsNav.goToMain')}
            >
              <SettingsContentPanel {...panel('general')}>
                <TargetGeneralPanel
                  hasProjectSelected={hasProjectSelected}
                  isAiApplicationTarget={isAiApplicationTarget}
                  appTargetId={appTargetId}
                  isReadOnly={isTargetReadOnly}
                  onOpenApiDocumentation={() => setActiveSection('documents')}
                />
              </SettingsContentPanel>

              <SettingsContentPanel {...panel('source-code')}>
                {applicationId ? (
                  <TargetSourceCodePanel
                    applicationId={applicationId}
                    webSettingsPath={webSourceCodeSettingsPath}
                    isReadOnly={isTargetReadOnly}
                  />
                ) : null}
              </SettingsContentPanel>

              <SettingsContentPanel {...panel('networking')}>
                {applicationId ? (
                  <TargetNetworkingPanel
                    applicationId={applicationId}
                    isReadOnly={isTargetReadOnly}
                  />
                ) : null}
              </SettingsContentPanel>

              <SettingsContentPanel {...panel('domains')}>
                <TargetDomainsPanel
                  hasProjectSelected={hasProjectSelected}
                  isLoading={form.domainsLoading}
                  isReadOnly={isTargetReadOnly}
                  domains={form.domainScopeTableDomains}
                  discoveredSubdomains={form.discoveredSubdomains}
                  applicationUrl={form.applicationUrl}
                  targetUrl={selectedTarget?.url}
                  redirectToUrl={selectedTarget?.redirectToUrl}
                  onDomainsChange={form.handleDomainScopeTableChange}
                />
              </SettingsContentPanel>

              <SettingsContentPanel {...panel('signal-categories')}>
                <TargetSignalCategoriesPanel isReadOnly={isTargetReadOnly} />
              </SettingsContentPanel>

              <SettingsContentPanel {...panel('deployment-delay')}>
                {applicationId ? (
                  <TargetDeploymentDelayPanel
                    applicationId={applicationId}
                    isReadOnly={isTargetReadOnly}
                  />
                ) : null}
              </SettingsContentPanel>

              <SettingsContentPanel {...panel('credentials')} keepMounted>
                <TargetCredentialsPanel
                  hasProjectSelected={hasProjectSelected}
                  isLoading={form.credentialsLoading}
                  isReadOnly={isTargetReadOnly}
                  applicationId={applicationId}
                  credentials={form.credentials}
                  credentialActions={credentialActions}
                  roleActions={roleActions}
                  refetchRoles={form.refetchRoles}
                  subscribe={subscribe}
                  customRoles={form.customRoles}
                />
              </SettingsContentPanel>

              <SettingsContentPanel {...panel('roles')}>
                <TargetRolesPanel
                  applicationId={applicationId}
                  hasProjectSelected={hasProjectSelected}
                  isLoading={form.rolesLoading}
                  isReadOnly={isTargetReadOnly}
                  roles={form.customRoles}
                  onRolesChange={form.setCustomRoles}
                  roleActions={roleActions}
                  hasGitIntegration={form.hasGitIntegration}
                  refetchRoles={form.refetchRoles}
                  credentials={form.credentials}
                />
              </SettingsContentPanel>

              <SettingsContentPanel {...panel('instructions')}>
                <TestingInstructionsTab isReadOnly={isTargetReadOnly} />
              </SettingsContentPanel>

              <SettingsContentPanel {...panel('guardrails')}>
                <TestingGuardrailsTab isReadOnly={isTargetReadOnly} />
              </SettingsContentPanel>

              {!isAiApplicationTarget ? (
                <SettingsContentPanel {...panel('memories')}>
                  <Box h="full" w="full" overflow="hidden">
                    <MemoriesContent />
                  </Box>
                </SettingsContentPanel>
              ) : null}

              {isPreventionWafEnabled && !isAiApplicationTarget ? (
                <SettingsContentPanel {...panel('prevention')}>
                  {applicationId ? (
                    <TargetPreventionPanel
                      applicationId={applicationId}
                      isReadOnly={isTargetReadOnly}
                    />
                  ) : null}
                </SettingsContentPanel>
              ) : null}

              <SettingsContentPanel {...panel('automation')}>
                <AutomationTab
                  isReadOnly={isTargetReadOnly}
                  isAiApplicationTarget={isAiApplicationTarget}
                />
              </SettingsContentPanel>

              <SettingsContentPanel {...panel('documents')}>
                {applicationId ? (
                  <TargetDocumentationPanel
                    applicationId={applicationId}
                    isBackendOnly={Boolean(selectedTarget?.isBackendOnly)}
                    isReadOnly={isTargetReadOnly}
                  />
                ) : null}
              </SettingsContentPanel>

              <SettingsContentPanel {...panel('knowledge-sources')}>
                {applicationId ? (
                  <TargetKnowledgeSourcesPanel
                    applicationId={applicationId}
                    isAiApplication={isAiApplicationTarget}
                    isReadOnly={isTargetReadOnly}
                  />
                ) : null}
              </SettingsContentPanel>

              <SettingsContentPanel {...panel('scoping')}>
                <Box h="full" w="full" overflow="hidden">
                  <ScopingContent />
                </Box>
              </SettingsContentPanel>

              <SettingsContentPanel {...panel('cvss')}>
                {applicationId ? (
                  <TargetCvssPanel
                    applicationId={applicationId}
                    isReadOnly={isTargetReadOnly}
                  />
                ) : null}
              </SettingsContentPanel>

              <SettingsContentPanel {...panel('copilot-skills')}>
                <Box h="full" w="full" overflow="hidden">
                  <SkillsSettingsTabContent />
                </Box>
              </SettingsContentPanel>

              {isMcpServersEnabled ? (
                <SettingsContentPanel {...panel('mcp-servers')}>
                  {appTargetId ? (
                    <ApplicationMcpServersTab applicationId={appTargetId} />
                  ) : (
                    <SettingsEmptyState>
                      {tSettings('mcpServerForm.applicationSelectRequired')}
                    </SettingsEmptyState>
                  )}
                </SettingsContentPanel>
              ) : null}

              {isMcpServerTarget && appTargetId ? (
                <SettingsContentPanel {...panel('mcp-connection')}>
                  <AiApplicationMcpConnectionTab
                    aiApplicationId={appTargetId}
                    isReadOnly={isTargetReadOnly}
                  />
                </SettingsContentPanel>
              ) : null}

              {canDeleteApplication && selectedTarget ? (
                <SettingsContentPanel {...panel('danger')}>
                  <TargetDangerPanel
                    applicationId={selectedTarget.id}
                    applicationName={selectedTarget.name}
                    targetType={targetType}
                  />
                </SettingsContentPanel>
              ) : null}
            </ErrorBoundary>
          </TargetSettingsSectionFrame>
        </SettingsTabHeaderActionsProvider>
      </VStack>
    </SettingsPageShell>
  );
}
